import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import CalcDetail from './CalcDetail';

/**
 * Day-of-week analysis chart
 * Shows average orders per weekday with busiest day highlighted
 */
export default function DayOfWeekChart({ data }) {
  if (!data || data.length === 0) {
    return (
      <div className="chart-container day-of-week-chart">
        <h3>Órdenes por Día de la Semana</h3>
        <div className="empty-state">
          <p>No hay datos suficientes para mostrar el análisis por día</p>
        </div>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      return (
        <div className="chart-tooltip">
          <p className="tooltip-date">{item.dayFull}</p>
          <p style={{ color: '#1976d2' }}>
            <strong>{item.avgOrders}</strong> órdenes promedio
          </p>
          <p style={{ color: '#6c6c7b', fontSize: '0.85rem' }}>
            Total: {item.totalOrders} en {item.weeksCount} {item.weeksCount === 1 ? 'semana' : 'semanas'}
          </p>
        </div>
      );
    }
    return null;
  };

  // Find max value for domain and calculate nice round number
  const maxOrders = Math.max(...data.map((d) => d.avgOrders));
  // Round up to nearest 5 or 10 for cleaner axis
  const niceMax = maxOrders <= 10
    ? Math.ceil(maxOrders / 5) * 5 || 5
    : Math.ceil(maxOrders / 10) * 10;
  const yAxisMax = Math.max(niceMax, 5); // Minimum of 5

  // Generate aria label with data summary
  const busiestDay = data.find((d) => d.isBusiest);
  const ariaLabel = `Gráfica de barras mostrando promedio de órdenes por día de la semana. ${
    busiestDay ? `El día más ocupado es ${busiestDay.dayFull} con ${busiestDay.avgOrders} órdenes promedio.` : ''
  }`;

  return (
    <div className="chart-container day-of-week-chart" role="figure" aria-label={ariaLabel}>
      <div className="day-of-week-header">
        <div>
          <h3>Promedio de Órdenes por Día</h3>
          <CalcDetail>
            Promedio = total de ordenes ese dia de la semana ÷ numero de semanas con datos. Ej: 3 viernes con 10, 14, 12 ordenes → promedio = 12.
          </CalcDetail>
        </div>
        <div className="busiest-day-badge">
          {busiestDay && (
            <span>
              Día más ocupado: <strong>{busiestDay.dayFull}</strong>
            </span>
          )}
        </div>
      </div>

      <ResponsiveContainer width="100%" height={280}>
        <BarChart
          data={data}
          margin={{ top: 10, right: 10, left: -10, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e8ebf2" vertical={false} />
          <XAxis
            dataKey="day"
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#6c6c7b', fontSize: 13 }}
          />
          <YAxis
            domain={[0, yAxisMax]}
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#6c6c7b', fontSize: 12 }}
            width={40}
            tickCount={5}
            allowDecimals={false}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar
            dataKey="avgOrders"
            radius={[6, 6, 0, 0]}
            maxBarSize={50}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.isBusiest ? '#1976d2' : '#c5d9f1'}
                stroke={entry.isBusiest ? '#1565c0' : 'transparent'}
                strokeWidth={entry.isBusiest ? 2 : 0}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
